// Gradient engine. A ramp is an ordered list of hex stops, sampled with
// piecewise-linear interpolation in sRGB. Traversal parameter t runs 0..1
// along the curve. This is the CPU reference implementation; the WebGL
// renderer bakes the same math into a 1D texture + uniforms.
import type { Closure, ColorMode, GradientState, RGB } from '../types.ts';
import { helixAt } from './helix.ts';

const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

export function hexToRgb(h: string): RGB {
  const s = String(h).trim().replace(/^#/, '');
  const v = s.length === 3 ? s.split('').map((c) => c + c).join('') : s.padEnd(6, '0').slice(0, 6);
  const n = parseInt(v, 16);
  if (!isFinite(n)) return [0, 0, 0];
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

export function rgbToHex(c: RGB): string {
  const h = (x: number) => Math.max(0, Math.min(255, Math.round(x))).toString(16).padStart(2, '0');
  return '#' + h(c[0]) + h(c[1]) + h(c[2]);
}

export function cssRgb(c: RGB): string {
  return 'rgb(' + Math.round(c[0]) + ',' + Math.round(c[1]) + ',' + Math.round(c[2]) + ')';
}

/**
 * The concrete stop list used for sampling. A closed curve needs a closed
 * colour path, or the basepoint shows up as a seam that belongs to the palette
 * rather than the geometry.
 */
export function buildClosedStops(stopsHex: string[], reverse: boolean, cyclic: boolean, closure: Closure): RGB[] {
  let stops = stopsHex.map(hexToRgb);
  if (stops.length === 0) stops = [[0, 0, 0]];
  if (stops.length === 1) stops = [stops[0], stops[0]];
  if (reverse) stops = stops.slice().reverse();
  if (cyclic) {
    if (closure === 'wrap') stops = stops.concat([stops[0]]);
    else if (closure === 'mirror') stops = stops.concat(stops.slice(0, -1).reverse());
  }
  return stops;
}

export function sampleRamp(stops: RGB[], t: number): RGB {
  const n = stops.length - 1;
  const x = Math.max(0, Math.min(1, t)) * n;
  const i = Math.min(n - 1, Math.floor(x));
  const f = x - i;
  const c0 = stops[i], c1 = stops[i + 1];
  return [lerp(c0[0], c1[0], f), lerp(c0[1], c1[1], f), lerp(c0[2], c1[2], f)];
}

export function nearestStop(stops: RGB[], t: number): RGB {
  const m = stops.length - 1;
  const i = Math.max(0, Math.min(m, Math.round(Math.max(0, Math.min(1, t)) * m)));
  return stops[i];
}

/** Map traversal t to ramp position u after rotation, repeat and ping-pong. */
export function gradientU(t: number, rotation: number, repeat: number, pingpong: boolean): number {
  const reps = Math.max(1, repeat | 0);
  const tr = t + (rotation || 0);
  const v = (tr - Math.floor(tr)) * reps;
  if (v >= reps) return pingpong && reps > 1 && (reps - 1) % 2 === 1 ? 0 : 1;
  if (pingpong && reps > 1) {
    const cyc = Math.floor(v);
    let u = v - cyc;
    if (cyc % 2 === 1) u = 1 - u;   // walk the ramp backwards on odd cycles
    return u;
  }
  return v - Math.floor(v);
}

export function helixTurns(repeat: number): number {
  return Math.max(1, Math.min(24, repeat | 0));
}

/** Memoizes the closed stop list for the current gradient state. */
export class RampCache {
  private key = '';
  private stops: RGB[] = [];
  get(g: GradientState, cyclic: boolean): RGB[] {
    const key = g.stops.join(',') + '|' + (g.reverse ? 1 : 0) + '|' + (cyclic ? 1 : 0) + '|' + g.closure;
    if (key !== this.key) {
      this.key = key;
      this.stops = buildClosedStops(g.stops, g.reverse, cyclic, g.closure);
    }
    return this.stops;
  }
}

/** Colour at traversal position t (0..1). Not meaningful for 'solid'. */
export function colorAt(t: number, cyclic: boolean, g: GradientState, mode: ColorMode, cache: RampCache): RGB {
  if (mode === 'helix') return helixAt(t, helixTurns(g.repeat));
  const stops = cache.get(g, cyclic);
  const u = gradientU(t, g.rotation, g.repeat, g.pingpong);
  const c = g.hard ? nearestStop(stops, u) : sampleRamp(stops, u);
  return [Math.round(c[0]), Math.round(c[1]), Math.round(c[2])];
}
